import {
  setQueryParams,
  // variantFirstAvailableOrDefault,
} from '@process-creative/slate-theme-tools';

// eslint-disable-next-line import/no-cycle
import { Product } from '../../tools/product/product';
import { Variant } from '../../types/variant';

const SEL_ELEMENT_DATA = '[data-variant-selector-data]';

type VariantSelectorContainer = HTMLInputElement | HTMLSelectElement;

export const EVT_VARIANT_CHANGED = 'onVariantChange';

const variantFirstAvailableOrDefault = ({
  variants,
}: {
  variants: Variant[];
}) => {
  const defaultVariant = variants.find((vrnt) => vrnt.available) || variants[0];
  return defaultVariant;
};
export class EventVariantChanged extends CustomEvent<{
  variantSelector: VariantSelector;
  oldVariant: UpdatedVariant;
  newVariant: UpdatedVariant;
}> {}

export type UpdatedVariant = Variant & {
  preorder_message?: string | null;
  subtitle?: string | null;
  public_title: string;
};

export class VariantSelector {
  public readonly container: HTMLElement;
  public readonly variantSelector: VariantSelectorContainer;
  public readonly data: Product;
  private readonly elementData: HTMLScriptElement;
  private updateHistory: boolean;

  constructor(container: HTMLElement, updateHistory = true) {
    this.container = container;
    this.updateHistory = updateHistory;
    this.variantSelector = container.querySelector('[name="id"]')!;
    this.elementData =
      container.querySelector<HTMLScriptElement>(SEL_ELEMENT_DATA)!;
    this.data = JSON.parse(this.elementData.innerHTML);

    this.variantSelector.addEventListener('change', this.onInputChanged);
  }

  public getVariantId(): number {
    return parseInt(this.variantSelector.value, 10);
  }

  public getVariant(): UpdatedVariant {
    const id = this.getVariantId();
    return this.data.variants.find((vrnt) => vrnt.id === id)!;
  }

  public setVariantId(id: number) {
    if (isNaN(id) || !isFinite(id)) return;
    if (id.toString() === this.variantSelector.value) return;
    if (!this.data.variants.some((vrnt) => vrnt.id === id)) return;

    const oldVariant = this.getVariant();

    this.variantSelector.value = id.toString();
    if (this.updateHistory) {
      if (
        id ===
        variantFirstAvailableOrDefault({ variants: this.data.variants }).id
      ) {
        setQueryParams({ variant: null });
      } else {
        setQueryParams({ variant: id.toString() });
      }
    }
    this.onVariantSelected();

    const newVariant = this.getVariant();
    const evt = new EventVariantChanged(EVT_VARIANT_CHANGED, {
      bubbles: true,
      cancelable: false,
      detail: {
        oldVariant,
        newVariant,
        variantSelector: this,
      },
    });
    this.variantSelector.dispatchEvent(evt);
  }

  public getOptionValues() {
    const values: { [key: string]: string[] } = {};

    this.data.options.forEach((option) => (values[option] = []));

    this.data.variants.forEach((variant) => {
      variant.options.forEach((opt, i) => {
        const key = this.data.options[i];
        if (values[key].indexOf(opt) !== -1) return;
        values[key].push(opt);
      });
    });

    return values;
  }

  public setVariantOption(index: number, value: string) {
    const currentVariant = this.getVariant();

    const intended: string[] = [];

    for (let i = 0; i < this.data.options.length; i++) {
      let selection: string;
      if (i === index) {
        selection = value;
      } else {
        selection = currentVariant.options[i];
      }
      intended.push(selection);
    }

    // Try find variant
    const newVariant = this.data.variants.find((vrnt) => {
      return vrnt.options.every((opt, optIdx) => {
        if (optIdx >= this.data.options.length) return true;
        return intended[optIdx] === opt;
      });
    });

    if (!newVariant) return null;

    this.setVariantId(newVariant.id);
    return newVariant;
  }

  public onVariantSelected() {}

  public dispose() {
    this.variantSelector.removeEventListener('change', this.onInputChanged);
  }

  private onInputChanged = (evt: Event) => {
    this.setVariantId(parseInt(this.variantSelector.value, 10));
  };
}
